import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, KeyRound, Loader2, Pencil, PlugZap, Plus, Trash2, XCircle } from 'lucide-react';
import { agentApi } from '../agentApi';
import { PROVIDER_ORDER } from '../agentContextMap';

const EMPTY = { name: '', provider: 'anthropic', model: '', apiKey: '', baseUrl: '', temperature: '', maxSteps: '25', instructions: '', isDefault: false, isEnabled: true };

function TestResult({ result }) {
  if (!result) return null;
  if (result.pending) {
    return (
      <span className="dagent-inline-status">
        <Loader2 size={13} className="dagent-spin" aria-hidden="true" /> Testing…
      </span>
    );
  }
  return result.ok ? (
    <span className="dagent-inline-status dagent-ok">
      <CheckCircle2 size={13} aria-hidden="true" />
      {result.toolCalling ? 'Connected, tool calling works' : 'Connected, but the model skipped the test tool'} · {(result.latencyMs / 1000).toFixed(1)} s
    </span>
  ) : (
    <span className="dagent-inline-status dagent-bad">
      <XCircle size={13} aria-hidden="true" /> {result.error}
    </span>
  );
}

function ProfileForm({ providers, initial, onCancel, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() =>
    initial
      ? {
          ...EMPTY,
          ...initial,
          apiKey: '',
          baseUrl: initial.baseUrl || '',
          temperature: initial.temperature ?? '',
          maxSteps: String(initial.maxSteps ?? 25),
          instructions: initial.instructions || '',
        }
      : EMPTY,
  );
  const [test, setTest] = useState(null);
  const [saving, setSaving] = useState(false);
  const provider = providers.find((candidate) => candidate.id === form.provider) || providers[0];
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));

  const payload = () => ({
    name: form.name.trim(),
    provider: form.provider,
    model: form.model.trim(),
    ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
    baseUrl: form.baseUrl.trim() || null,
    temperature: form.temperature === '' ? null : Number(form.temperature),
    maxSteps: Number(form.maxSteps) || 25,
    instructions: form.instructions.trim() || null,
    isDefault: form.isDefault,
    isEnabled: form.isEnabled,
  });

  const runTest = async () => {
    setTest({ pending: true });
    try {
      if (!form.apiKey.trim() && editing) setTest(await agentApi.testProfile(initial.id));
      else setTest(await agentApi.testUnsavedProfile({ provider: form.provider, model: form.model.trim(), apiKey: form.apiKey.trim(), baseUrl: form.baseUrl.trim() || null }));
    } catch (error) {
      setTest({ ok: false, error: error.issues?.map((issue) => `${issue.path}: ${issue.message}`).join('; ') || error.message });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await agentApi.updateProfile(initial.id, payload());
      else await agentApi.createProfile(payload());
      toast.success(editing ? 'Agent updated' : 'Agent added');
      onSaved();
    } catch (error) {
      toast.error(error.issues?.map((issue) => `${issue.path}: ${issue.message}`).join('; ') || error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dagent-form dagent-card">
      <h4>{editing ? `Edit ${initial.name}` : 'Add an agent'}</h4>
      <div className="dagent-form__grid">
        <label>
          Name
          <input className="dagent-input" value={form.name} onChange={set('name')} placeholder="Front desk (Claude)" />
        </label>
        <label>
          Provider
          <select className="dagent-select" value={form.provider} onChange={set('provider')}>
            {[...providers]
              .sort((a, b) => PROVIDER_ORDER.indexOf(a.id) - PROVIDER_ORDER.indexOf(b.id))
              .map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.label}
                </option>
              ))}
          </select>
        </label>
        <label>
          Model
          <input className="dagent-input dagent-mono" list="dagent-models" value={form.model} onChange={set('model')} placeholder={provider?.suggestedModels?.[0]} />
          <datalist id="dagent-models">
            {(provider?.suggestedModels || []).map((model) => (
              <option key={model} value={model} />
            ))}
          </datalist>
        </label>
        <label>
          API key
          <input
            className="dagent-input dagent-mono"
            type="password"
            autoComplete="off"
            value={form.apiKey}
            onChange={set('apiKey')}
            placeholder={editing ? `Leave blank to keep ${initial.apiKeyHint}` : provider?.keyPlaceholder}
          />
        </label>
      </div>
      <details className="dagent-advanced">
        <summary>Advanced</summary>
        <div className="dagent-form__grid">
          <label>
            Base URL
            <input className="dagent-input dagent-mono" value={form.baseUrl} onChange={set('baseUrl')} placeholder={provider?.defaultBaseUrl} />
          </label>
          <label>
            Temperature
            <input className="dagent-input" type="number" step="0.1" min="0" max="2" value={form.temperature} onChange={set('temperature')} placeholder="Provider default" />
          </label>
          <label>
            Max steps per request
            <input className="dagent-input" type="number" min="1" max="100" value={form.maxSteps} onChange={set('maxSteps')} />
          </label>
        </div>
        <label>
          Property instructions (added to the system prompt)
          <textarea className="dagent-input" rows={4} value={form.instructions} onChange={set('instructions')} placeholder="e.g. Late check-out until 12:00 is free for settled folios." />
        </label>
      </details>
      <div className="dagent-form__row">
        <label className="dagent-check-all">
          <input type="checkbox" checked={form.isDefault} onChange={set('isDefault')} /> Default agent
        </label>
        <label className="dagent-check-all">
          <input type="checkbox" checked={form.isEnabled} onChange={set('isEnabled')} /> Enabled
        </label>
        <span className="dagent-spacer" />
        <TestResult result={test} />
      </div>
      <p className="dagent-muted dagent-small">
        <KeyRound size={12} aria-hidden="true" /> Keys are encrypted on the server and never shown again. {provider?.docsUrl && <a href={provider.docsUrl} target="_blank" rel="noopener noreferrer">Provider docs</a>}
      </p>
      <div className="dagent-form__actions">
        <button type="button" className="dagent-btn dagent-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="dagent-btn dagent-btn--ghost" onClick={runTest} disabled={!form.model || (!form.apiKey && !editing) || test?.pending}>
          <PlugZap size={14} aria-hidden="true" /> Test connection
        </button>
        <button type="button" className="dagent-btn dagent-btn--primary" onClick={save} disabled={saving || !form.name || !form.model || (!editing && !form.apiKey)}>
          {editing ? 'Save' : 'Add agent'}
        </button>
      </div>
    </div>
  );
}

function AgentSettings({ onChanged }) {
  const [profiles, setProfiles] = useState([]);
  const [providers, setProviders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tests, setTests] = useState({});
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    try {
      const [profileData, settingsData] = await Promise.all([agentApi.listProfiles(), agentApi.getSettings()]);
      setProfiles(profileData.profiles);
      setProviders(profileData.providers);
      setSettings(settingsData.settings);
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    setEditing(null);
    load();
    onChanged?.();
  };

  const test = async (profile) => {
    setTests((current) => ({ ...current, [profile.id]: { pending: true } }));
    try {
      const result = await agentApi.testProfile(profile.id);
      setTests((current) => ({ ...current, [profile.id]: result }));
    } catch (error) {
      setTests((current) => ({ ...current, [profile.id]: { ok: false, error: error.message } }));
    }
  };

  const remove = async (profile) => {
    if (!window.confirm(`Delete the agent "${profile.name}"? Its key is deleted with it.`)) return;
    try {
      await agentApi.deleteProfile(profile.id);
      changed();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const saveSettings = async () => {
    try {
      const { settings: saved } = await agentApi.updateSettings({
        publicEnabled: settings.publicEnabled,
        publicProfileId: settings.publicProfileId || null,
        paymentLinkTemplate: settings.paymentLinkTemplate,
        whatsappPaymentTemplate: settings.whatsappPaymentTemplate || null,
      });
      setSettings(saved);
      toast.success('Settings saved');
      onChanged?.();
    } catch (error) {
      toast.error(error.issues?.map((issue) => `${issue.path}: ${issue.message}`).join('; ') || error.message);
    }
  };

  return (
    <div className="dagent-settings">
      <section className="dagent-panel">
        <div className="dagent-panel__bar">
          <div>
            <h3>Agents and models</h3>
            <p className="dagent-muted dagent-small">Anthropic, OpenAI, Google Gemini, Meta Muse Spark and Groq. Staff choose between enabled agents.</p>
          </div>
          <span className="dagent-spacer" />
          <button type="button" className="dagent-btn dagent-btn--primary dagent-btn--sm" onClick={() => setEditing({})}>
            <Plus size={14} aria-hidden="true" /> Add agent
          </button>
        </div>

        {editing && <ProfileForm providers={providers} initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={changed} />}

        <div className="dagent-table-wrap">
          <table className="dagent-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Provider</th>
                <th>Model</th>
                <th>Key</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile.id}>
                  <td>
                    <strong>{profile.name}</strong>
                    {profile.isDefault && <span className="dagent-badge dagent-badge--succeeded">Default</span>}
                    <div>
                      <TestResult result={tests[profile.id]} />
                    </div>
                  </td>
                  <td>{profile.providerLabel}</td>
                  <td className="dagent-mono">{profile.model}</td>
                  <td className="dagent-mono">{profile.apiKeyHint}</td>
                  <td>{profile.isEnabled ? 'Enabled' : 'Disabled'}</td>
                  <td className="dagent-nowrap">
                    <button type="button" className="dagent-icon-btn" title="Test connection" onClick={() => test(profile)}>
                      <PlugZap size={15} aria-hidden="true" />
                    </button>
                    <button type="button" className="dagent-icon-btn" title="Edit" onClick={() => setEditing(profile)}>
                      <Pencil size={15} aria-hidden="true" />
                    </button>
                    <button type="button" className="dagent-icon-btn" title="Delete" onClick={() => remove(profile)}>
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {profiles.length === 0 && (
                <tr>
                  <td colSpan={6} className="dagent-muted">
                    No agents yet. Add one with your provider API key to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {settings && (
        <section className="dagent-panel">
          <div className="dagent-panel__bar">
            <h3>Guest messages and public assistant</h3>
          </div>
          <div className="dagent-form">
            <label>
              Payable link template
              <input
                className="dagent-input dagent-mono"
                value={settings.paymentLinkTemplate}
                onChange={(event) => setSettings({ ...settings, paymentLinkTemplate: event.target.value })}
              />
              <span className="dagent-muted dagent-small">Use {'{bookingId}'} or {'{bookingNumber}'}. The web check-in page collects pending balances.</span>
            </label>
            <label>
              WhatsApp template name (WATI)
              <input
                className="dagent-input dagent-mono"
                value={settings.whatsappPaymentTemplate || ''}
                placeholder="Leave empty to allow email only"
                onChange={(event) => setSettings({ ...settings, whatsappPaymentTemplate: event.target.value })}
              />
              <span className="dagent-muted dagent-small">Parameters sent: name, booking_number, amount, link.</span>
            </label>
            <div className="dagent-form__row">
              <label className="dagent-check-all">
                <input type="checkbox" checked={settings.publicEnabled} onChange={(event) => setSettings({ ...settings, publicEnabled: event.target.checked })} />
                Public guest assistant
              </label>
              <select
                className="dagent-select"
                value={settings.publicProfileId || ''}
                onChange={(event) => setSettings({ ...settings, publicProfileId: event.target.value || null })}
                aria-label="Agent used by the public assistant"
              >
                <option value="">Choose agent…</option>
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name}
                  </option>
                ))}
              </select>
            </div>
            <p className="dagent-muted dagent-small">
              Read-only answers about availability, rooms, courses and services at <span className="dagent-mono">POST /api/agent-public/chat</span>. Not linked from the booking engine yet.
            </p>
            <div className="dagent-form__actions">
              <button type="button" className="dagent-btn dagent-btn--primary" onClick={saveSettings}>
                Save settings
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default AgentSettings;
